/* eslint-disable react/jsx-no-useless-fragment */
import { useQuery } from '../useQuery';
import { Ability, defineAbility } from '@casl/ability';
import { createContextualCan } from '@casl/react';
import { getUserPermissions } from '@dispatch/shared/services';
import {
  Actions,
  Permission,
  SnackBarNotification,
  Subjects,
} from '@dispatch/shared/types';
import React, { createContext } from 'react';
import { Navigate } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { useCurrentUserResource } from './UseCurrentUserResource';

export interface AbilityContextI {
  permissions: Ability<[Actions, Subjects]> | undefined;
  setPermissions: React.Dispatch<Ability<[Actions, Subjects]>>;
}

export const AbilityContext = createContext<AbilityContextI | undefined>(
  undefined
);

/**
 * Context provider to allow child components to access users permissions.
 */
function PermissionProvider({ children }: { children: React.ReactChild }) {
  const [permissions, setPermissions] =
    React.useState<Ability<[Actions, Subjects]>>();
  const value = { permissions, setPermissions };

  return (
    <AbilityContext.Provider value={value}>{children}</AbilityContext.Provider>
  );
}

/**
 * useBuiltPermissions gets the current users permissions, and uses them to define
 * abilities for the user.
 */
const useBuildPermissions = (userId: string, isAccessTokenSet: boolean) => {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();

  const userPermissions = useQuery(
    ['user-permissions', userId],
    () => getUserPermissions(userId),
    {
      staleTime: 5000,
      enabled:
        isAuthenticated &&
        inProgress === InteractionStatus.None &&
        isAccessTokenSet,
    }
  );

  return {
    userPermissions:
      userPermissions?.data?.data && //required as you can't call .length on undefined
      userPermissions.data.data.length > 0
        ? defineAbility<Ability<[Actions, Subjects]>>((can) => {
            userPermissions?.data?.data?.forEach((permission: Permission) => {
              const [action, ...rest] = permission.operationId.split('-');

              can(action as Actions, rest.join('-') as Subjects);
            });
          })
        : null,
    // This is needed as it returns error when enabled parameter is false
    status:
      isAuthenticated && inProgress === InteractionStatus.None
        ? userPermissions.status
        : 'loading',
  };
};

/**
 * usePermissions is a hook that returns the permissions context
 */
function usePermissions() {
  const context = React.useContext(AbilityContext);

  if (context === undefined) {
    throw new Error(
      'usePermissions must be used within a permissions provider'
    );
  }
  return context;
}

/**
 * Can is a wrapper component that will check the permissions of tue user, and insure they have the proper
 * rights to access the page they are trying to navigate to.
 */
const CanNavigateToPage = ({
  children,
  I,
  can,
  reRoute = '/invalid-permissions',
  snackNotifications,
  showNotification,
  checkAdjuster,
}: {
  children: React.ReactNode;
  I: Actions;
  can: Subjects;
  reRoute?: string;
  snackNotifications: SnackBarNotification;
  showNotification?: boolean;
  checkAdjuster?: boolean;
}) => {
  const context = React.useContext(AbilityContext);
  const { isAdjuster } = useCurrentUserResource();

  // if no permissions exist or they do not have proper permissions, then reroute.
  if (
    !context?.permissions ||
    !context?.permissions.can(I, can) ||
    (checkAdjuster && isAdjuster)
  ) {
    if (showNotification) {
      snackNotifications.error(
        `Error`,
        'You are not authorized to visit this page',
        {
          preventDuplicate: true,
        }
      );
    }

    return <Navigate to={reRoute} replace={true} />;
  }

  // if they are permitted to view the page then allow them to access
  return <>{children}</>; // using div causes styling issues
};

const Can = createContextualCan<any>(AbilityContext.Consumer);

export {
  PermissionProvider,
  useBuildPermissions,
  usePermissions,
  CanNavigateToPage,
  Can,
};
